import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Search, Loader2, Printer, Eye, Trash2, X } from 'lucide-react';
import { usePayoutVouchers, useTrashPayoutVouchers } from '../../services/httpServices/queries';
import { diagnosticService, type DoctorPayment } from '../../services/httpServices/diagnosticService';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { printPdfBlobUrl } from '../../utils/printDocument';
import {
  money,
  isReferralVoucher,
  voucherPayeeName,
  PAYOUT_PAGE_SIZE,
  type PayeeKind,
} from './payoutShared';
import { PayeeTypeBadge } from './payoutBadges';
import { PayoutVoucherModal } from './PayoutVoucherModal';

/**
 * Payout vouchers — **one ledger** of everything we have paid.
 *
 * Referral-partner (`RP-…`) vouchers are served by the payee-agnostic
 * `billing/payouts` endpoint. Doctors are paid through the same referral
 * ledger, so there is no separate doctor voucher entry.
 */
export function PayoutVouchers({
  onViewReceipt,
  payeeType,
}: {
  onViewReceipt?: (id: string) => void;
  /** Restrict the ledger to one payee kind (e.g. a referral-only operator). */
  payeeType?: PayeeKind;
}) {
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 400);
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const headerRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = usePayoutVouchers({
    payeeType,
    search: debounced.trim() || undefined,
    page,
    limit: PAYOUT_PAGE_SIZE,
  });

  const vouchers = useMemo(() => data?.data ?? [], [data]);
  const totalAmount = data?.stats?.totalAmount;

  const trashVouchers = useTrashPayoutVouchers();

  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('billing:settlement:delete');
  const showSelect = canDelete;

  // Drop selected ids that scrolled off the current page.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.length === 0) return prev;
      const visible = new Set(vouchers.map((v) => v.id));
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [vouchers]);

  const allSelected = vouchers.length > 0 && vouchers.every((v) => selectedIds.includes(v.id));
  const someSelected = selectedIds.length > 0;
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  const toggleAll = () => setSelectedIds(allSelected ? [] : vouchers.map((v) => v.id));
  const toggleOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await trashVouchers.mutateAsync(selectedIds);
      const trashed = res.data?.trashed?.length ?? 0;
      toast.success(
        `Moved ${trashed} voucher${trashed === 1 ? '' : 's'} to Trash. Restore from the Trash tab.`,
      );
      setSelectedIds([]);
      setConfirmDelete(false);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to move vouchers to trash.',
      );
    }
  };

  const handlePrint = async (voucher: DoctorPayment) => {
    try {
      const url = await diagnosticService.printPayoutVoucher(voucher.id);
      await printPdfBlobUrl(url, { onError: (m) => toast.error(m) });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate voucher PDF');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-text-muted">
          Total paid: <span className="font-semibold text-text">{money(totalAmount)}</span>
        </div>
        <div className="flex items-center gap-2 flex-1 justify-end">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by voucher number or payee…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
        </div>
      </div>

      {showSelect && someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> voucher(s) selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={trashVouchers.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              <Trash2 size={14} />
              Move to Trash
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors flex items-center gap-1.5"
            >
              <X size={14} />
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-text-muted">
                {showSelect && (
                  <th className="px-4 py-3 w-10">
                    <input
                      ref={headerRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={vouchers.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                )}
                <th className="px-4 py-3 font-medium">Voucher #</th>
                <th className="px-4 py-3 font-medium">Payee</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Method</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && vouchers.length === 0 ? (
                <tr>
                  <td colSpan={showSelect ? 8 : 7} className="px-4 py-10 text-center text-text-muted">
                    <Loader2 size={18} className="animate-spin inline mr-2" />
                    Loading…
                  </td>
                </tr>
              ) : vouchers.length === 0 ? (
                <tr>
                  <td colSpan={showSelect ? 8 : 7} className="px-4 py-10 text-center text-text-muted">
                    No payout vouchers yet. Settle a payee to issue the first voucher.
                  </td>
                </tr>
              ) : (
                vouchers.map((voucher) => {
                  const referral = isReferralVoucher(voucher);
                  const checked = selectedIds.includes(voucher.id);
                  return (
                    <tr
                      key={voucher.id}
                      className={`border-b border-border last:border-0 hover:bg-background/60 ${
                        checked ? 'bg-primary/5' : ''
                      }`}
                    >
                      {showSelect && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(voucher.id)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                            title="Select voucher"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 font-medium text-text">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setDetailId(voucher.id)}
                            title="View voucher details"
                            className="group inline-flex items-center gap-1.5 font-mono text-primary transition-colors hover:underline"
                          >
                            {voucher.paymentNumber}
                          </button>
                          {voucher.isOpening && (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[0.625rem] font-medium bg-warning/10 text-warning">
                              OPENING
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text">{voucherPayeeName(voucher)}</td>
                      <td className="px-4 py-3">
                        <PayeeTypeBadge payeeType={referral ? 'REFERRAL' : 'DOCTOR'} />
                      </td>
                      <td className="px-4 py-3 text-text-muted">{voucher.method || 'CASH'}</td>
                      <td className="px-4 py-3 text-text-muted">
                        {voucher.paidAt ? new Date(voucher.paidAt).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-text">
                        {money(voucher.amount)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          {referral && (
                            <button
                              type="button"
                              onClick={() => onViewReceipt?.(voucher.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                            >
                              <Eye size={14} />
                              Receipt
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handlePrint(voucher)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-text text-xs font-medium hover:bg-background transition-colors"
                          >
                            <Printer size={14} />
                            Print / PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {data?.meta && (
          <Pagination
            page={data.meta.page}
            totalPages={data.meta.totalPages}
            total={data.meta.total}
            limit={data.meta.limit}
            onPageChange={setPage}
            itemName="vouchers"
          />
        )}
      </div>


      {detailId && <PayoutVoucherModal id={detailId} onClose={() => setDetailId(null)} />}

      <ConfirmDialog
        open={showSelect && confirmDelete}
        title="Move vouchers to Trash?"
        description={`${selectedIds.length} voucher(s) will be reversed and moved to Trash. Their shares return to Outstanding and can be restored from the Trash tab.`}
        confirmLabel="Move to Trash"
        variant="danger"
        loading={trashVouchers.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
